import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import type { FormType } from '#types/System.types';
import type { Workflow, WorkflowTriggerType } from '#types';
import type { ArTableId } from '#types/System.Artifacts.types';
import { createCollectionStore } from '../store';

type UseApplicableWorkflowsParams = {
  triggerType: Exclude<WorkflowTriggerType, 'both'>;
  formType?: FormType['docId'];
  tableId?: ArTableId;
  enabled?: boolean;
};

const workflowsStore = createCollectionStore<Workflow>('workflows');

export function useApplicableWorkflows({
  triggerType,
  formType,
  tableId,
  enabled = true,
}: UseApplicableWorkflowsParams): {
  workflows: Workflow[];
  loading: boolean;
} {
  useEffect(() => {
    if (!enabled) return;
    workflowsStore.connect();
    return () => workflowsStore.disconnect();
  }, [enabled]);

  const snap = useSnapshot(workflowsStore.state);

  const workflows = useMemo(() => {
    const all: Workflow[] = [];
    for (const id of snap.docIds) {
      const entry = snap.docs.get(id);
      if (entry) all.push({ ...(entry.data as Workflow), docId: id });
    }

    const includesTrigger = (w: Workflow): boolean => {
      if (w.triggerType === 'both') return true;
      return w.triggerType === triggerType;
    };

    return all
      .filter((w) => includesTrigger(w))
      .filter((w) => {
        if (!formType) return true;
        if (!w.applicableFormTypes || w.applicableFormTypes.length === 0) return true;
        return w.applicableFormTypes.includes(formType);
      })
      .filter((w) => {
        if (!tableId) return true;
        if (!w.applicableTableTypes || w.applicableTableTypes.length === 0) return true;
        return w.applicableTableTypes.includes(tableId);
      })
      .sort((a, b) => {
        const aLabel = a.description?.shortLabel ?? a.docId ?? '';
        const bLabel = b.description?.shortLabel ?? b.docId ?? '';
        return aLabel.localeCompare(bLabel);
      });
  }, [snap.docIds, snap.docs, formType, tableId, triggerType]);

  return {
    workflows,
    loading: snap.loading,
  };
}
